import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Tooltip, Typography } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import { getCitasCalendario, getTareasCalendario, getTrabajadores } from '../../services/apis';
import {
    formatearIdDia,
    normalizarCitasCalendario,
    normalizarTareasCalendario,
    ordenarEventosCalendario,
} from './calendarioUtils';
import { textoMayusculas } from '../../utils/utils';

const normalizarTextoFiltro = (texto = '') =>
    String(texto || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLocaleLowerCase('es-ES');

const trabajadorAsignadoAUsuario = (evento, nombreUsuario) => {
    const nombreNormalizado = normalizarTextoFiltro(nombreUsuario);

    if (!nombreNormalizado) return false;

    return String(evento.trabajadores_asignados || '')
        .split(',')
        .map((trabajador) => normalizarTextoFiltro(trabajador))
        .filter(Boolean)
        .includes(nombreNormalizado);
};

export const ResumenCalendarioHoy = ({ idUsuarioActual, nombreUsuarioActual: nombreUsuarioProp = '' }) => {
    const [eventos, setEventos] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const idHoy = useMemo(() => formatearIdDia(new Date()), []);

    useEffect(() => {
        const cargarResumen = async () => {
            setLoaded(false);

            try {
                const [responseTareas, responseCitas, responseTrabajadores] = await Promise.all([
                    getTareasCalendario(),
                    getCitasCalendario(),
                    getTrabajadores(),
                ]);

                const trabajadorActual = (responseTrabajadores?.data || []).find((trabajador) =>
                    String(trabajador.id) === String(idUsuarioActual)
                );
                const nombreUsuarioActual = trabajadorActual?.nombre || nombreUsuarioProp;
                const tareas = normalizarTareasCalendario(responseTareas?.data || []);
                const citas = normalizarCitasCalendario(responseCitas?.data || []);
                const eventosHoy = [...tareas, ...citas]
                    .filter((evento) => evento.fecha === idHoy)
                    .filter((evento) => trabajadorAsignadoAUsuario(evento, nombreUsuarioActual))
                    .sort(ordenarEventosCalendario);

                setEventos(eventosHoy);
            } catch (error) {
                console.error('Error al cargar el resumen del calendario:', error);
                setEventos([]);
            } finally {
                setLoaded(true);
            }
        };

        cargarResumen();
    }, [idHoy, idUsuarioActual, nombreUsuarioProp]);

    return (
        <Card
            sx={{
                width: '100%',
                maxWidth: '100%',
                borderRadius: '14px',
                border: '1px solid rgba(229, 57, 53, 0.12)',
                boxShadow: '0 8px 18px rgba(0, 0, 0, 0.08)',
                background: 'var(--fondoTarjeta)',
                overflow: 'hidden',
            }}
        >
            <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '190px minmax(0, 1fr)' },
                        gap: { xs: 1.5, md: 2 },
                        alignItems: 'start',
                        minWidth: 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventNoteIcon color="primary" sx={{ flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                                Calendario de hoy
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                {eventos.length} {eventos.length === 1 ? 'cita' : 'citas'}
                            </Typography>
                        </Box>
                    </Box>

                    {!loaded ? (
                        <Box sx={{ minHeight: 54, display: 'grid', placeItems: 'center' }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : eventos.length === 0 ? (
                        <Box
                            sx={{
                                minHeight: 54,
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                fontWeight: 600,
                            }}
                        >
                            No hay citas para hoy.
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    lg: 'repeat(3, minmax(0, 1fr))',
                                },
                                gap: 1,
                                maxHeight: { xs: 500, sm: 240, md: 178 },
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                pr: 0.5,
                                minWidth: 0,
                            }}
                        >
                            {eventos.map((evento) => {
                                const finalizado = Number(evento.finalizado || 0) === 1;
                                const titulo = `${evento.hora_inicio ? `${evento.hora_inicio} - ` : ''}${evento.titulo}`;

                                return (
                                    <Box
                                        key={`${evento.tipo_calendario}-${evento.id}`}
                                        sx={{
                                            minWidth: 0,
                                            borderRadius: '8px',
                                            border: finalizado
                                                ? '1px solid rgba(46, 125, 50, 0.24)'
                                                : '1px solid rgba(229, 57, 53, 0.18)',
                                            background: finalizado ? '#edf6f0' : '#fff',
                                            p: 1,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, minWidth: 0 }}>
                                            {finalizado ? (
                                                <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 16, flexShrink: 0 }} />
                                            ) : (
                                                <AccessTimeIcon sx={{ color: '#e53935', fontSize: 16, flexShrink: 0 }} />
                                            )}
                                            <Tooltip title={titulo}>
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 800,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {titulo}
                                                </Typography>
                                            </Tooltip>
                                        </Box>

                                        {(evento.cliente || evento.trabajadores_asignados) && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mt: 0.55,
                                                    minWidth: 0,
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {evento.cliente && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, minWidth: 0 }}>
                                                        <PersonIcon sx={{ fontSize: 12, color: '#666', flexShrink: 0 }} />
                                                        <Typography noWrap sx={{ fontSize: 10.5, color: '#555', minWidth: 0 }}>
                                                            {textoMayusculas(evento.cliente)}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {evento.trabajadores_asignados && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, minWidth: 0 }}>
                                                        <GroupsIcon sx={{ fontSize: 12, color: '#666', flexShrink: 0 }} />
                                                        <Typography noWrap sx={{ fontSize: 10.5, color: '#555', minWidth: 0 }}>
                                                            {evento.trabajadores_asignados}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};
